import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, CircleMarker, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarker,
  faExternalLinkAlt,
  faChevronLeft,
  faChevronRight,
  faInfoCircle,
  faCalendarCheck,
  faCalendarDay,
  faMapMarkerAlt,
  faHotel,
  faBuilding,
  faDirections,
  faClock,
  faList,
  faLayerGroup,
  faTimes,
  faCheckCircle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import TourContext from '../../context/TourContext';
import UserContext from '../../context/UserContext';
import moment from 'moment';
import styles from './Route.module.css';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';

// Fix Leaflet's default icon issues
let DefaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Map Updater component to handle map updates without re-rendering
const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  
  return null;
};

function Route() {
  const { getSelectedDate, selectedEvent } = useContext(TourContext);
  const { userData } = useContext(UserContext);
  
  // Map state
  const [center, setCenter] = useState([0, 0]);
  const [zoom, setZoom] = useState(13);
  const mapInstance = useRef(null);
  
  // UI state
  const [showVenueCard, setShowVenueCard] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [showLegend, setShowLegend] = useState(true);
  const [showScheduleList, setShowScheduleList] = useState(true);
  const [showVenueMarkers, setShowVenueMarkers] = useState(true);
  const [activeScheduleId, setActiveScheduleId] = useState(null);
  const [activePopup, setActivePopup] = useState(null);
  
  // Data state
  const [schedules, setSchedules] = useState([]);
  const [eventVenue, setEventVenue] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);
  
  // Get event date data
  const eventDate = getSelectedDate();
  const eventType = selectedEvent?.type || 'tour';
  
  // Get current time (for determining active schedule)
  const currentTime = useMemo(() => moment(), []);
  
  // Fetch schedules and hotels directly from subcollections
  useEffect(() => {
    // Only run this effect if we haven't fetched data yet or if eventDate/selectedEvent changed
    if (dataFetched && !eventDate?.dateLastUpdated) return;
    
    const fetchSubcollections = async () => {
      if (!selectedEvent?.id || !eventDate?.id) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      
      try {
        const db = getFirestoreInstance();
        const collectionName = eventType === 'tour' ? 'tours' : 'shows';
        
        // Fetch schedules from schedule subcollection
        const schedulesQuery = query(
          collection(db, collectionName, selectedEvent.id, 'eventDates', eventDate.id, 'schedule'),
          orderBy('createdAt', 'desc')
        );
        
        const schedulesSnapshot = await getDocs(schedulesQuery);
        
        if (!schedulesSnapshot.empty) {
          const schedulesList = schedulesSnapshot.docs.map(doc => ({
            id: doc.id,
            docId: doc.id,  // Keep docId for backward compatibility
            ...doc.data()
          }));
          
          // Process schedules to add lat/lng if missing
          const processedSchedules = schedulesList.filter(schedule => 
            schedule.fromLocation && 
            schedule.fromLocation.displayName && 
            schedule.fromLocation.formattedAddress &&
            schedule.toLocation && 
            schedule.toLocation.displayName && 
            schedule.toLocation.formattedAddress &&
            (userData.userType === 'Manager' ? true : schedule.visibility !== 'Me' || schedule.creatorId === userData.userId)
          ).map(schedule => {
            // Ensure fromLocation has latitude/longitude
            if (schedule.fromLocation && !schedule.fromLocation.latitude && !schedule.fromLocation.longitude) {
              if (schedule.fromLocation.location) {
                schedule.fromLocation.latitude = schedule.fromLocation.location.latitude;
                schedule.fromLocation.longitude = schedule.fromLocation.location.longitude;
              }
            }
            
            // Ensure toLocation has latitude/longitude
            if (schedule.toLocation && !schedule.toLocation.latitude && !schedule.toLocation.longitude) {
              if (schedule.toLocation.location) {
                schedule.toLocation.latitude = schedule.toLocation.location.latitude;
                schedule.toLocation.longitude = schedule.toLocation.location.longitude;
              }
            }
            
            return schedule;
          }).sort((a, b) => {
            if (a.startTimeDayAfter !== b.startTimeDayAfter) {
              return a.startTimeDayAfter ? 1 : -1;
            }
            const timeA = moment(a.startTime, 'HH:mm');
            const timeB = moment(b.startTime, 'HH:mm');
            return timeA.isBefore(timeB) ? -1 : 1;
          });
          
          setSchedules(processedSchedules);
          
          // Set active schedule based on current time
          if (processedSchedules.length > 0) {
            const today = moment().format('YYYY-MM-DD');
            const active = processedSchedules.find(schedule => {
              const startTime = moment(`${today} ${schedule.startTime}`, 'YYYY-MM-DD HH:mm');
              const endTime = moment(`${today} ${schedule.endTime}`, 'YYYY-MM-DD HH:mm');
              
              // If current time is between start and end time
              return currentTime.isBetween(startTime, endTime);
            });
            
            // If there's an active schedule, set it
            if (active) {
              setActiveScheduleId(active.id || active.docId);
              
              // Center map on active schedule's from location
              if (active.fromLocation?.latitude && active.fromLocation?.longitude) {
                setCenter([active.fromLocation.latitude, active.fromLocation.longitude]);
              }
            } else {
              // If no active schedule, get the next upcoming one
              const upcoming = processedSchedules.find(schedule => {
                const startTime = moment(`${today} ${schedule.startTime}`, 'YYYY-MM-DD HH:mm');
                return currentTime.isBefore(startTime);
              });
              
              if (upcoming) {
                setActiveScheduleId(upcoming.id || upcoming.docId);
                
                // Center map on upcoming schedule's from location
                if (upcoming.fromLocation?.latitude && upcoming.fromLocation?.longitude) {
                  setCenter([upcoming.fromLocation.latitude, upcoming.fromLocation.longitude]);
                }
              } else {
                // If no active or upcoming schedule, use the first one
                const first = processedSchedules[0];
                if (first && first.fromLocation?.latitude && first.fromLocation?.longitude) {
                  setCenter([first.fromLocation.latitude, first.fromLocation.longitude]);
                }
              }
            }
          }
        } else {
          setSchedules([]);
        }
        
        // Fetch hotels from hotels subcollection
        const hotelsQuery = query(
          collection(db, collectionName, selectedEvent.id, 'eventDates', eventDate.id, 'hotels')
        );
        
        const hotelsSnapshot = await getDocs(hotelsQuery);
        
        let hotelList = [];
        if (!hotelsSnapshot.empty) {
          hotelList = hotelsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Set hotel state
          if (hotelList.length > 0) {
            setHotels(hotelList);
            
            // Center on first hotel if no schedules
            if (schedules.length === 0 && hotelList[0]?.venueDetails?.venueData) {
              const hotelVenue = hotelList[0].venueDetails.venueData;
              const latitude = hotelVenue.location?.latitude || hotelVenue.latitude;
              const longitude = hotelVenue.location?.longitude || hotelVenue.longitude;
              
              if (latitude && longitude && center[0] === 0 && center[1] === 0) {
                setCenter([latitude, longitude]);
              }
            }
          } else {
            setHotels([]);
          }
        } else {
          setHotels([]);
        }
        
        // Get event venue data
        if (eventDate.event?.venueDetails?.venueData) {
          const venue = eventDate.event.venueDetails.venueData;
          // Check for both location objects and direct lat/lng properties
          const latitude = venue.location?.latitude || venue.latitude;
          const longitude = venue.location?.longitude || venue.longitude;
          
          if (latitude && longitude) {
            setEventVenue({
              id: venue.id,
              displayName: venue.displayName,
              formattedAddress: venue.formattedAddress,
              latitude: latitude,
              longitude: longitude,
              photoUrl: venue.placeCoverPhotoUrl || venue.photoUrl
            });
            
            // If no schedules or hotels and center is not set, center map on venue
            if (schedules.length === 0 && hotelList.length === 0 && center[0] === 0 && center[1] === 0) {
              setCenter([latitude, longitude]);
            }
          }
        }
        
        // Mark data as fetched to prevent repeated fetching
        setDataFetched(true);
      } catch (error) {
        console.error('Error fetching subcollections:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSubcollections();
  }, [selectedEvent?.id, eventDate?.id, eventDate?.dateLastUpdated, userData.userType, userData.userId, currentTime, eventType]);
  
  // Default to showing venue markers if there are no schedules
  useEffect(() => {
    if (schedules.length === 0 && (eventVenue || hotels.length > 0)) {
      setShowVenueMarkers(true);
      
      // If there's an event venue, center on it
      if (eventVenue && eventVenue.latitude && eventVenue.longitude && center[0] === 0 && center[1] === 0) {
        setCenter([eventVenue.latitude, eventVenue.longitude]);
      }
      // Otherwise center on hotel if available
      else if (hotels.length > 0 && hotels[0].venueDetails?.venueData && center[0] === 0 && center[1] === 0) {
        const hotelVenue = hotels[0].venueDetails.venueData;
        const latitude = hotelVenue.location?.latitude || hotelVenue.latitude;
        const longitude = hotelVenue.location?.longitude || hotelVenue.longitude;
        
        if (latitude && longitude) {
          setCenter([latitude, longitude]);
        }
      }
    }
  }, [schedules.length, eventVenue, hotels, center]);
  
  // Handle marker click
  const handleMarkerClick = (venue) => {
    setSelectedVenue(venue);
    setShowVenueCard(true);
    setActivePopup(venue); // Track which popup is active
    
    // Center map on the marker's position
    if (venue.latitude && venue.longitude) {
      setCenter([venue.latitude, venue.longitude]);
    }
  };
  
  // Handle close venue card
  const handleCloseVenueCard = () => {
    setShowVenueCard(false);
    setSelectedVenue(null);
    setActivePopup(null); // Clear active popup tracking
    
    // Close any open popups on the map
    if (mapInstance.current) {
      mapInstance.current.closePopup();
    }
  };
  
  // Handle opening in Google Maps
  const handleOpenInGoogleMaps = (venue) => {
    if (!venue) return;
    
    const searchQuery = encodeURIComponent(`${venue.displayName}, ${venue.formattedAddress}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${searchQuery}`, '_blank');
  };
  
  // Navigate to venue details page
  const navigateToVenueDetails = (venue) => {
    if (!venue || !venue.id) return;
    
    // Navigate to venue details page
    window.location.href = `/venue/${venue.id}`;
  };
  
  // Handle getting directions
  const handleGetDirections = (from, to) => {
    if (!from || !to) return;
    
    const origin = encodeURIComponent(`${from.displayName}, ${from.formattedAddress}`);
    const destination = encodeURIComponent(`${to.displayName}, ${to.formattedAddress}`);
    window.open(`https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`, '_blank');
  };
  
  // Handle schedule card click
  const handleScheduleCardClick = (schedule) => {
    // Set active schedule
    setActiveScheduleId(schedule.id || schedule.docId);
    
    // Center map on schedule's from location
    if (schedule.fromLocation?.latitude && schedule.fromLocation?.longitude) {
      setCenter([schedule.fromLocation.latitude, schedule.fromLocation.longitude]);
    }
  };
  
  // Render marker for a location
  const renderLocationMarker = (location, type, isActive = false) => {
    if (!location || !location.latitude || !location.longitude) return null;
    
    // Check if location coordinates are valid numbers
    const lat = parseFloat(location.latitude);
    const lng = parseFloat(location.longitude);
    
    if (isNaN(lat) || isNaN(lng)) {
      console.warn(`Invalid coordinates for ${type} location:`, location);
      return null;
    }
    
    // Define marker style based on type
    const getMarkerStyle = () => {
      switch (type) {
        case 'from':
          return {
            fillColor: '#1a73e8', // Blue for schedule markers
            color: 'white',
            fillOpacity: 0.8,
            weight: 2,
            radius: isActive ? 10 : 8
          };
        case 'to':
          return {
            fillColor: '#1a73e8', // Blue for schedule markers
            color: 'white',
            fillOpacity: 0.8,
            weight: 2,
            radius: isActive ? 10 : 8
          };
        case 'event':
          return {
            fillColor: '#E2384D', // Red for event venue
            color: 'white',
            fillOpacity: 0.8,
            weight: 2,
            radius: 12
          };
        case 'hotel':
          return {
            fillColor: '#4caf50', // Green for hotel
            color: 'white',
            fillOpacity: 0.8,
            weight: 2,
            radius: 12
          };
        default:
          return {
            fillColor: '#1a73e8',
            color: 'white',
            fillOpacity: 0.8,
            weight: 2,
            radius: 8
          };
      }
    };
    
    const isThisPopupActive = activePopup && 
      activePopup.latitude === location.latitude &&
      activePopup.longitude === location.longitude;
    
    return (
      <CircleMarker
        center={[location.latitude, location.longitude]}
        pathOptions={getMarkerStyle()}
        eventHandlers={{
          click: () => handleMarkerClick({...location, type})
        }}
      >
        <Popup
          onClose={() => {
            // When popup is closed manually, also close the venue card
            if (isThisPopupActive) {
              setShowVenueCard(false);
              setSelectedVenue(null);
              setActivePopup(null);
            }
          }}
        >
          <div className={styles.markerPopup}>
            <h3>{location.displayName}</h3>
            <p>{location.formattedAddress}</p>
            <div className={styles.popupType}>
              {type === 'from' && 'From Location'}
              {type === 'to' && 'To Location'}
              {type === 'event' && 'Event Venue'}
              {type === 'hotel' && 'Hotel'}
            </div>
          </div>
        </Popup>
      </CircleMarker>
    );
  };
  
  // Render polyline between two locations
  const renderPolyline = (from, to, isActive = false) => {
    if (!from || !to || !from.latitude || !from.longitude || !to.latitude || !to.longitude) return null;
    
    const positions = [
      [from.latitude, from.longitude],
      [to.latitude, to.longitude]
    ];
    
    return (
      <Polyline
        positions={positions}
        pathOptions={{
          color: isActive ? '#E2384D' : '#1a73e8',
          weight: isActive ? 4 : 3,
          opacity: isActive ? 1 : 0.7,
          dashArray: isActive ? '' : '5, 5'
        }}
      />
    );
  };
  
  // Format time for display
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return moment(timeStr, 'HH:mm').format('h:mm A');
  };
  
  // Render legend
  const renderLegend = () => {
    if (!showLegend) return null;
    
    return (
      <div className={styles.mapLegend}>
        <div className={styles.legendHeader}>
          <h3>Map Legend</h3>
          <button 
            className={styles.legendCloseButton}
            onClick={() => setShowLegend(false)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendMarker} style={{ backgroundColor: '#1a73e8' }}></div>
          <span>Schedule Locations</span>
        </div>
        {showVenueMarkers && (
          <>
            <div className={styles.legendItem}>
              <div className={styles.legendMarker} style={{ backgroundColor: '#E2384D', border: '2px solid white', width: '14px', height: '14px' }}></div>
              <span>Event Venue</span>
            </div>
            <div className={styles.legendItem}>
              <div className={styles.legendMarker} style={{ backgroundColor: '#4caf50', border: '2px solid white', width: '14px', height: '14px' }}></div>
              <span>Hotel</span>
            </div>
          </>
        )}
      </div>
    );
  };
  
  // Render venue card
  const renderVenueCard = () => {
    if (!showVenueCard || !selectedVenue) return null;
    
    return (
      <div className={styles.venueCard}>
        <div className={styles.venueCardHeader}>
          <h3>{selectedVenue.displayName}</h3>
          <button 
            className={styles.venueCardClose}
            onClick={handleCloseVenueCard}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className={styles.venueCardContent}>
          <p className={styles.venueAddress}>{selectedVenue.formattedAddress}</p>
          <div className={styles.venueType}>
            {selectedVenue.type === 'from' && 'From Location'}
            {selectedVenue.type === 'to' && 'To Location'}
            {selectedVenue.type === 'event' && 'Event Venue'}
            {selectedVenue.type === 'hotel' && 'Hotel'}
          </div>
          <div className={styles.venueActions}>
            <button 
              className={styles.venueAction}
              onClick={() => handleOpenInGoogleMaps(selectedVenue)}
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <span>View on Maps</span>
            </button>
            
            {selectedVenue.id && (
              <button 
                className={styles.venueAction}
                onClick={() => navigateToVenueDetails(selectedVenue)}
              >
                <FontAwesomeIcon icon={faBuilding} />
                <span>Venue Details</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // Render controls
  const renderControls = () => {
    return (
      <div className={styles.mapControls}>
        <button 
          className={`${styles.controlButton} ${!showLegend ? styles.active : ''}`}
          onClick={() => setShowLegend(!showLegend)}
          title="Toggle Legend"
        >
          <FontAwesomeIcon icon={faLayerGroup} />
        </button>
        <button 
          className={`${styles.controlButton} ${!showScheduleList ? styles.active : ''}`}
          onClick={() => setShowScheduleList(!showScheduleList)}
          title="Toggle Schedule List"
        >
          <FontAwesomeIcon icon={faList} />
        </button>
        <button 
          className={`${styles.controlButton} ${!showVenueMarkers ? styles.active : ''}`}
          onClick={() => setShowVenueMarkers(!showVenueMarkers)}
          title="Toggle Venue Markers"
        >
          <FontAwesomeIcon icon={faBuilding} />
        </button>
      </div>
    );
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <FontAwesomeIcon icon={faSpinner} spin className={styles.loadingSpinner} />
        <p>Loading map data...</p>
      </div>
    );
  }

  // If no data at all (no schedules, no event venue, no hotel)
  if (!schedules.length && !eventVenue && !hotels.length) {
    return (
      <div className={styles['empty-state']}>
        <FontAwesomeIcon icon={faMapMarker} />
        <p>No locations found for this date. Add schedules, event venue, or hotel to see the map.</p>
      </div>
    );
  }

  return (
    <div className={styles['route-container']}>
      {/* Map Section */}
      <div className={styles['map-section']}>
        {renderControls()}
        
        <MapContainer 
          center={center}
          zoom={zoom}
          className={styles['leaflet-map']}
          zoomControl={false}
          ref={(map) => {
            mapInstance.current = map;
          }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ZoomControl position="bottomright" />
          <MapUpdater center={center} zoom={zoom} />
          
          {/* Render schedule markers and routes */}
          {schedules.map((schedule) => {
            const isActive = (schedule.id || schedule.docId) === activeScheduleId;
            return (
              <React.Fragment key={schedule.id || schedule.docId}>
                {schedule.fromLocation && schedule.fromLocation.latitude && schedule.fromLocation.longitude && 
                  renderLocationMarker(schedule.fromLocation, 'from', isActive)}
                {schedule.toLocation && schedule.toLocation.latitude && schedule.toLocation.longitude && 
                  renderLocationMarker(schedule.toLocation, 'to', isActive)}
                {renderPolyline(schedule.fromLocation, schedule.toLocation, isActive)}
              </React.Fragment>
            );
          })}
          
          {/* Render event venue marker if enabled */}
          {showVenueMarkers && eventVenue && eventVenue.latitude && eventVenue.longitude && 
            renderLocationMarker(eventVenue, 'event')}
          
          {/* Render hotel markers if enabled */}
          {showVenueMarkers && hotels.length > 0 && hotels.map(hotel => {
            if (hotel?.venueDetails?.venueData) {
              const venueData = hotel.venueDetails.venueData;
              const hotelVenue = {
                id: venueData.id,
                displayName: venueData.displayName,
                formattedAddress: venueData.formattedAddress,
                latitude: venueData.location?.latitude || venueData.latitude,
                longitude: venueData.location?.longitude || venueData.longitude,
                photoUrl: venueData.placeCoverPhotoUrl || venueData.photoUrl
              };
              
              if (hotelVenue.latitude && hotelVenue.longitude) {
                return renderLocationMarker(hotelVenue, 'hotel');
              }
            }
            return null;
          })}
          
          {renderLegend()}
        </MapContainer>
        
        {renderVenueCard()}
      </div>

      {/* Schedule List Section */}
      {showScheduleList && schedules.length > 0 && (
        <div className={styles['schedule-list-section']}>
          <div className={styles['schedule-list-header']}>
            <h3>Today's Schedule</h3>
            <button 
              className={styles['schedule-list-toggle']}
              onClick={() => setShowScheduleList(false)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className={styles['schedule-cards']}>
            {schedules.map((schedule) => {
              const isActive = (schedule.id || schedule.docId) === activeScheduleId;
              return (
                <div 
                  key={schedule.id || schedule.docId}
                  className={`${styles['schedule-card']} ${isActive ? styles['active'] : ''}`}
                  onClick={() => handleScheduleCardClick(schedule)}
                >
                  <div className={styles['schedule-card-header']}>
                    <h4>{schedule.title}</h4>
                    {isActive && (
                      <div className={styles['active-badge']}>
                        <FontAwesomeIcon icon={faCheckCircle} />
                        <span>Current</span>
                      </div>
                    )}
                  </div>
                  
                  <div className={styles['schedule-card-time']}>
                    <FontAwesomeIcon icon={faClock} />
                    <span>{formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}</span>
                  </div>
                  
                  <div className={styles['schedule-card-locations']}>
                    <div className={styles['location-item']}>
                      <div className={styles['location-marker']} style={{ backgroundColor: '#1a73e8' }}></div>
                      <div className={styles['location-info']}>
                        <span className={styles['location-label']}>From:</span>
                        <span className={styles['location-name']}>{schedule.fromLocation.displayName}</span>
                      </div>
                    </div>
                    
                    <div className={styles['location-item']}>
                      <div className={styles['location-marker']} style={{ backgroundColor: '#1a73e8' }}></div>
                      <div className={styles['location-info']}>
                        <span className={styles['location-label']}>To:</span>
                        <span className={styles['location-name']}>{schedule.toLocation.displayName}</span>
                      </div>
                    </div>
                  </div>
                  
                  <button 
                    className={styles['directions-button']}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGetDirections(schedule.fromLocation, schedule.toLocation);
                    }}
                  >
                    <FontAwesomeIcon icon={faDirections} />
                    <span>Get Directions</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Show venue/hotel info when no schedules */}
      {showScheduleList && schedules.length === 0 && (eventVenue || hotels.length > 0) && (
        <div className={styles['schedule-list-section']}>
          <div className={styles['schedule-list-header']}>
            <h3>Locations</h3>
            <button 
              className={styles['schedule-list-toggle']}
              onClick={() => setShowScheduleList(false)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className={styles['schedule-cards']}>
            {eventVenue && (
              <div 
                className={styles['schedule-card']}
                onClick={() => handleMarkerClick(eventVenue)}
              >
                <div className={styles['schedule-card-header']}>
                  <h4>Event Venue</h4>
                </div>
                
                <div className={styles['location-item']}>
                  <div className={styles['location-marker']} style={{ backgroundColor: '#E2384D' }}></div>
                  <div className={styles['location-info']}>
                    <span className={styles['location-name']}>{eventVenue.displayName}</span>
                    <span className={styles['location-address']}>{eventVenue.formattedAddress}</span>
                  </div>
                </div>
                
                <button 
                  className={styles['directions-button']}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenInGoogleMaps(eventVenue);
                  }}
                >
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  <span>View on Maps</span>
                </button>
                
                {eventVenue.id && (
                  <button 
                    className={styles['directions-button']}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateToVenueDetails(eventVenue);
                    }}
                  >
                    <FontAwesomeIcon icon={faBuilding} />
                    <span>Venue Details</span>
                  </button>
                )}
              </div>
            )}
            
            {hotels.map((hotel, index) => {
              if (!hotel?.venueDetails?.venueData) return null;
              
              const venueData = hotel.venueDetails.venueData;
              const hotelVenue = {
                id: venueData.id,
                displayName: venueData.displayName,
                formattedAddress: venueData.formattedAddress,
                latitude: venueData.location?.latitude || venueData.latitude,
                longitude: venueData.location?.longitude || venueData.longitude,
                photoUrl: venueData.placeCoverPhotoUrl || venueData.photoUrl
              };
              
              return (
                <div 
                  key={hotel.id || `hotel-${index}`}
                  className={styles['schedule-card']}
                  onClick={() => handleMarkerClick(hotelVenue)}
                >
                  <div className={styles['schedule-card-header']}>
                    <h4>Hotel</h4>
                  </div>
                  
                  <div className={styles['location-item']}>
                    <div className={styles['location-marker']} style={{ backgroundColor: '#4caf50' }}></div>
                    <div className={styles['location-info']}>
                      <span className={styles['location-name']}>{hotelVenue.displayName}</span>
                      <span className={styles['location-address']}>{hotelVenue.formattedAddress}</span>
                    </div>
                  </div>
                  
                  <button 
                    className={styles['directions-button']}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenInGoogleMaps(hotelVenue);
                    }}
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    <span>View on Maps</span>
                  </button>
                  
                  {hotelVenue.id && (
                    <button 
                      className={styles['directions-button']}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToVenueDetails(hotelVenue);
                      }}
                    >
                      <FontAwesomeIcon icon={faBuilding} />
                      <span>Venue Details</span>
                    </button>
                  )}
                </div>
              );
            })}
            
            {eventVenue && hotels.length > 0 && hotels[0]?.venueDetails?.venueData && (
              <div className={styles['schedule-card']}>
                <button 
                  className={styles['directions-button']}
                  onClick={(e) => {
                    e.stopPropagation();
                    const hotelVenue = hotels[0].venueDetails.venueData;
                    const hotelLocation = {
                      displayName: hotelVenue.displayName,
                      formattedAddress: hotelVenue.formattedAddress
                    };
                    handleGetDirections(eventVenue, hotelLocation);
                  }}
                >
                  <FontAwesomeIcon icon={faDirections} />
                  <span>Get Directions from Event to Hotel</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Route;